import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';

import { AnimatePresence } from 'framer-motion';

import AppState from '@webapp/store/state';
import { ResetError } from '@webapp/store/ui/action-creators';
import { CREATE_ARTIST_ERROR } from '@webapp/store/artist/actions';

import { ArtistType } from '@common/types/artist/artist-type';
import SimpleArtist from '@common/types/artist/simple-artist';
import { NewInstance } from '@common/types/data/new-instance';
import { ArtistPlatforms } from '@common/types/artist/artist-platforms';
import { StreamingPlatform } from '@common/types/data/streaming-platform';

import StreamingPlatformCtrl from '@webapp/controllers/form/streaming-platform-list';
import {
  validatePlatform,
  validateSpotifyUrl,
} from '@webapp/utils/validators/artist';

import AvatarEditor from '../../avatar-editor/index';

import InputField from '@components/form/input';
import ErrorMessage from '@components/basics/error-message';
import MotionOpacity from '@components/motion/motion-opacity';
import BoxTitleHeader from '@components/layout/box-title-header';

interface ArtistEditorCtrlProps extends Partial<NewInstance<SimpleArtist>> {
  className?: string;
  disableAvatar?: boolean;
  disableStreaming?: boolean;
  noHint?: boolean;
  hasTitle?: React.ReactNode;
  showErrors: boolean;
  artistCreateError?: string;
  hideStreamingPlatforms?: boolean;
  onClick?: () => void;
  onChange: (data: NewInstance<SimpleArtist>) => void;
  onIncompleteData?: () => void;
  // searchArtist: (artistName: string) => SearchExternalArtists;
  resetArtistCreateError: () => ResetError;
  name?: string;
}

const mapStateToProps = (state: AppState) => ({
  artistCreateError: state.ui.errors[CREATE_ARTIST_ERROR],
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  resetArtistCreateError: () => dispatch(new ResetError(CREATE_ARTIST_ERROR)),
  // searchArtist: (artistName: string) =>
  //   dispatch(new SearchExternalArtists(artistName)),
});

export const ONBOARDING_ARTIST_PLATFORMS: ArtistPlatforms[] = [
  'spotify',
  'apple',
  'soundcloud',
];

export const getStreamingPlatforms = (
  platforms: SimpleArtist['platforms'],
  showErrors: boolean = true
): StreamingPlatform[] => {
  return ONBOARDING_ARTIST_PLATFORMS.map((platform) => {
    const handle = (platforms ? platforms[platform] : '') || '';

    let error: JSX.Element | null = null;

    if (!validatePlatform(handle)) {
      error = (
        <MotionOpacity>
          <ErrorMessage
            className="mb-5"
            errorId="ONBOARDING.ARTIST.ERROR.URL"
          />
        </MotionOpacity>
      );
    }

    if (platform === 'spotify' && !handle.length) {
      error = (
        <MotionOpacity>
          <ErrorMessage
            className="mb-5"
            errorId="ONBOARDING.ARTIST.ERROR.STREAMING"
          />
        </MotionOpacity>
      );
    }

    if (
      platform === 'spotify' &&
      handle.length &&
      !validateSpotifyUrl(handle)
    ) {
      error = (
        <MotionOpacity>
          <ErrorMessage
            className="mb-5"
            errorId="ONBOARDING.ARTIST.ERROR.SPOTIFY_URL"
          />
        </MotionOpacity>
      );
    }

    return {
      platform,
      handle: handle,
      errorMessage: showErrors ? error : null,
    };
  });
};

export const ArtistEditorCtrl: React.FC<ArtistEditorCtrlProps> = (props) => {
  const propsName = props.name;
  const propsAvatar = props.avatar;
  // TODO: DEVTEAM-1057 - We need to review if we still need the type.
  // const propsType = props.type;
  const propPlatforms = props.platforms;

  const [name, setName] = React.useState(propsName || '');
  // const [type, setType] = React.useState(propsType || undefined);
  const [avatar, setAvatar] = React.useState(propsAvatar || undefined);
  const [platforms, setPlatforms] = React.useState<SimpleArtist['platforms']>(
    propPlatforms || {}
  );

  React.useEffect(() => setName(propsName || ''), [propsName]);
  // React.useEffect(() => setType(propsType), [propsType]);
  React.useEffect(() => setAvatar(propsAvatar), [propsAvatar]);
  React.useEffect(() => setPlatforms(propPlatforms), [propPlatforms]);

  const handleChange = (newVal?: Partial<NewInstance<SimpleArtist>>) => {
    const newArtist: NewInstance<SimpleArtist> = {
      // @ts-ignore
      name,
      // @ts-ignore
      type: 'solo' as ArtistType,
      // @ts-ignore
      avatar,
      ...(newVal as NewInstance<SimpleArtist>),
    };

    if (newArtist) {
      return props.onChange(newArtist);
    }

    if (props.onIncompleteData) {
      return props.onIncompleteData();
    }
  };

  const fieldChange = (
    field: keyof NewInstance<SimpleArtist>,
    setter: any,
    value: any
  ) => {
    setter(value);
    handleChange({
      [field]: value,
    });
  };

  const handleNameChange = (value: string) => {
    if (!!props.artistCreateError) {
      props.resetArtistCreateError();
    }
    handleChange({
      name: value,
    });

    setName(value);
    // props.searchArtist(value);
  };

  const handlePlatformUpdate = (newPlatforms: StreamingPlatform[]) => {
    const update = newPlatforms.reduce(
      (memo, p) => ({
        ...memo,
        [p.platform]: p.handle || '',
      }),
      {} as SimpleArtist['platforms']
    );
    setPlatforms(update);
    handleChange({ platforms: update });
  };

  return (
    <form
      autoComplete="off"
      className={props.className}
      onSubmit={(ev) => ev.preventDefault()}
    >
      {props.disableAvatar ? null : (
        <AvatarEditor
          className="mb-40"
          url={avatar}
          onChange={(url: string) => fieldChange('avatar', setAvatar, url)}
        />
      )}

      {props.hasTitle ? (
        <BoxTitleHeader title={props.hasTitle} className="mb-20" />
      ) : null}

      <AnimatePresence>
        <MotionOpacity>
          {props.showErrors && props.artistCreateError && (
            <ErrorMessage
              className="mt-40 mb-10"
              errorId={props.artistCreateError}
            />
          )}
          {props.showErrors && !name && (
            <ErrorMessage
              className="mt-40 mb-10"
              errorId="ONBOARDING.ARTIST.ERROR.NAME"
            />
          )}
          <InputField
            isGrey
            value={name}
            label={<Translate id="ARTIST.CREATE.ARTIST_BAND" />}
            onChange={(value) => handleNameChange(value)}
            onClick={props.onClick}
          />

          {!props.hideStreamingPlatforms && (
            <StreamingPlatformCtrl
              disableSelect
              className="mt-40"
              filterKeys={[]}
              platforms={getStreamingPlatforms(platforms, props.showErrors)}
              onChange={(newPlatforms) => {
                if (newPlatforms) {
                  handlePlatformUpdate(newPlatforms);
                } else {
                  setPlatforms(undefined);
                  handleChange({ platforms: undefined });
                }
              }}
            />
          )}
        </MotionOpacity>
      </AnimatePresence>
    </form>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ArtistEditorCtrl);
